import React, {ReactNode} from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DividerCustom from '@ui/dividerCustom/DividerCustom';
import {useTranslation} from 'react-i18next';
import {ITaskSingleVoyage} from "@containers/singleVoyage/tasks/types";
import CloseTaskSingleVoyage from "@containers/singleVoyage/tasks/closeTask/CloseTaskSingleVoyage";
import {sx} from './sx';

interface IProps {
    task: ITaskSingleVoyage
    accountId: string | number
    voyageId?: number
    /** Whole-task close - every stop the task still has open, in one decision. */
    canCloseTask?: boolean
    /** Whatever the planner adds: edit, delete. */
    extra?: ReactNode
    closeModalFn?: () => void
    closePopoverFn?: () => void
}

/**
 * Closing a task across every stop it still has open.
 *
 * This is the one action that needs a panel: it asks which outcome to write, it
 * says how many stops it will touch, and it cannot be undone. Closing a single
 * stop is a tick or a cross on the row itself - that one needs no confirmation
 * and should not cost a popover.
 *
 * closePopoverFn comes from the popover and is handed down: an action that
 * succeeds closes the panel it was pressed in.
 */
const TaskActionsPanelSingleVoyage = ({
                                          task,
                                          accountId,
                                          voyageId,
                                          canCloseTask,
                                          extra,
                                          closeModalFn,
                                          closePopoverFn,
                                      }: IProps) => {

    const {t} = useTranslation()

    return (
        <Stack sx={sx.panel}>
            <Typography sx={sx.panelTitle} noWrap>
                {task.title}
            </Typography>

            {canCloseTask && <CloseTaskSingleVoyage
                task={task}
                voyageId={voyageId}
                accountId={accountId}
                closeModalFn={closeModalFn}
                closePopoverFn={closePopoverFn}
            />}

            {extra && <>
                <DividerCustom/>
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'flex-end'}
                    gap={1}
                    sx={sx.panelExtra}
                >
                    <Typography sx={sx.panelExtraLabel}>
                        {t('singleVoyage.taskActions')}
                    </Typography>
                    {extra}
                </Stack>
            </>}
        </Stack>
    );
};

export default TaskActionsPanelSingleVoyage;
